/**
 * Client-side time limits for hotel calls. Each one is longer than the backend's worst case for
 * that call, so the browser never gives up while the backend can still answer.
 */
export const HOTEL_REQUEST_TIMEOUTS_MS = {
  /** Backend: up to 4 attempts × 30 s against the provider; a quote is safe to drop. */
  availability: 60_000,
  /**
   * Backend worst case for a booking without answer: 2 × (90 s book + verification by
   * clientReference) ≈ 4 min. Hitting this limit means the outcome is unknown (no retry).
   */
  booking: 270_000,
  /** Inquiry for non-refundable rates: only sends mails. */
  inquiry: 30_000,
  /** Search stream: longest silence (no response or no new event) before giving up. */
  searchIdle: 60_000,
} as const;

/** The request took longer than its client-side limit. */
export class HotelRequestTimeoutError extends Error {
  constructor(readonly timeoutMs: number) {
    super(`hotels.api.timeout:${timeoutMs}`);
    this.name = 'HotelRequestTimeoutError';
  }
}

/** `fetch` that rejects with `HotelRequestTimeoutError` once `timeoutMs` elapses. */
export async function fetchWithTimeout(
  input: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (controller.signal.aborted) throw new HotelRequestTimeoutError(timeoutMs);
    throw error;
  } finally {
    clearTimeout(timer);
  }
}
